'use client';

import { useTranslations } from 'next-intl';
import { Delete02Icon, Loading03Icon, StopIcon } from '@terra/ui-icons';
import { ListPageBulkActionButton } from '../../common/ListPageBulkActionButton';
import { ListPageSelectionBar } from '../../common/ListPageSelectionBar';

type AgentExecutionsBulkActionsBarProps = {
  selectedCount: number;
  abortableCount: number | null;
  isBulkAborting: boolean;
  isBulkDeleting: boolean;
  isBusy: boolean;
  mutationsDisabledTooltip: string | null;
  onClearSelection: () => void;
  onAbort: () => void;
  onDelete: () => void;
};

const SpinningIcon = () => (
  <Loading03Icon size={12} style={{ animation: 'spin 1s linear infinite' }} />
);

export function AgentExecutionsBulkActionsBar({
  selectedCount,
  abortableCount,
  isBulkAborting,
  isBulkDeleting,
  isBusy,
  mutationsDisabledTooltip,
  onClearSelection,
  onAbort,
  onDelete,
}: AgentExecutionsBulkActionsBarProps) {
  const t = useTranslations('proxy.executions.bulkActions');
  const tCommon = useTranslations('designSystem.common');
  const isLocked = isBusy || mutationsDisabledTooltip !== null;
  const abortLabel =
    abortableCount === null
      ? t('abortSelected')
      : t('abort', { count: abortableCount });

  return (
    <ListPageSelectionBar
      selectedCount={selectedCount}
      clearSelectionLabel={t('clearSelection')}
      onClearSelection={onClearSelection}
    >
      <ListPageBulkActionButton
        label={abortLabel}
        tooltip={mutationsDisabledTooltip ?? abortLabel}
        icon={isBulkAborting ? <SpinningIcon /> : <StopIcon size={12} />}
        isDisabled={isLocked || abortableCount === 0}
        onClick={onAbort}
      />
      <ListPageBulkActionButton
        label={tCommon('delete')}
        tooltip={mutationsDisabledTooltip ?? tCommon('delete')}
        icon={isBulkDeleting ? <SpinningIcon /> : <Delete02Icon size={12} />}
        isDisabled={isLocked}
        onClick={onDelete}
      />
    </ListPageSelectionBar>
  );
}
